<template>
	<div class="layout-topbar" tabindex="0">
		<div class="layout-topbar-left">
      <div class="layout-env-badge" v-if="envBadgeText">{{envBadgeText}}</div>
			<router-link class="layout-topbar-logo p-ripple" to="/" v-ripple><img id="logo" :src="whqLogoWhite" alt="WarehouseQuote Logo" /></router-link>
			<Button class="layout-menu-button p-button-rounded" icon="material-symbols-outlined chevron_right" :aria-label="`${menuActive ? 'Collapse' : 'Expand'} Menu`" :aria-expanded="menuActive" aria-controls="main-menu" @click="onMenuButtonClick($event)" />
			<Button class="layout-topbar-mobile-button p-button-text p-button-rounded" icon="material-symbols-outlined menu" :aria-label="`${menuActive ? 'Show' : 'Hide'} Topbar`" @click="onTopbarMobileButtonClick($event)" />
		</div>
		<div class="layout-topbar-right" :class="{'layout-topbar-mobile-active': props.mobileTopbarActive}">
			<div class="layout-topbar-actions-right ml-auto">
				<ul class="layout-topbar-items">
					<li class="layout-topbar-item layout-search-item">
            <Button class="layout-topbar-action p-button-text p-button-rounded mx-2" @click="onTopbarItemClick($event, 'search')" v-tooltip.bottom="'Search'" icon="material-symbols-outlined search" aria-label="Quick Search" ref="searchButton" />
						<transition name="layout-search-panel" @enter="onSearchTransitionEnter()">
							<div class="layout-search-panel shadow-6 p-input-group px-3" v-show="props.searchActive" @click="onSearchContainerClick" @keydown.esc="searchButton.$el.click()">
                <InputText id="search-panel-input" ref="searchInput" type="text" :placeholder="getSearchCriteriaPlaceholder()" :aria-label="getSearchCriteriaPlaceholder()" class="layout-search-panel-input mt-1 w-full" v-model="searchInputValue" @keydown.enter.prevent="onSearchEnterKey($event)" @keydown="submitButton.highlight($event)" @keyup="submitButton.attenuate($event)" autocomplete="off" />
                <div class="mb-3 pt-3 flex justify-content-center layout-search-buttons">
									<Button class="p-button-sm mr-3 px-4 p-button-rounded" :class="{ 'modifier-pressed': modifierKey === 'ctrl'}" icon="material-symbols-outlined search" label="Inventory" aria-label="Search Inventory" @click="onSearch({ type: 'inventory', field: 'searchCriteria' })" />
									<Button class="p-button-sm px-4 p-button-rounded" icon="material-symbols-outlined search" label="Orders" aria-label="Search Orders" @click="onSearch({ type: 'order', field: 'searchCriteria' })" />
									<Button class="p-button-sm ml-3 px-4 p-button-rounded" :class="{ 'modifier-pressed': modifierKey === 'alt'}" v-if="appState.user.allOrganizationCodes" icon="material-symbols-outlined search" label="Zendesk&nbsp;Tickets" aria-label="Search Zendesk Tickets"  @click="onSearch({ type: 'order', field: 'zendeskTicket' })" />
                </div>
							</div>
						</transition>
					</li>
					<li class="layout-topbar-item layout-settings-item">
            <Button class="layout-topbar-action p-button-text p-button-rounded mx-2" @click="onTopbarItemClick($event, 'support')" v-tooltip.bottom="'Support'" icon="material-symbols-outlined help" aria-label="Support" :disabled="!appState.zendeskWidget.isAvailable" ref="supportButton" />
					</li>
					<li class="layout-topbar-item layout-settings-item">
            <Button class="layout-topbar-action p-button-text p-button-rounded mx-2" @click="onTopbarItemClick($event, 'settings')" v-tooltip.bottom="'Settings'" icon="material-symbols-outlined settings" aria-label="Settings" ref="settingsButton" />
					</li>
					<li class="layout-topbar-item layout-settings-item mr-0 sm:mr-2">
            <Button class="layout-topbar-action p-button-text p-button-rounded mx-2" @click="onTopbarItemClick($event, 'sign-out')" v-tooltip.bottom="'Sign&nbsp;Out'" icon="material-symbols-outlined logout" aria-label="Sign Out" ref="signOutButton" data-cy="AppTopBar_sign-out-button" />
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

<script setup>
  import { ref, inject, onMounted, onUnmounted } from "vue";
  import { useRouter } from "vue-router";
  import { useConfirm } from "primevue/useconfirm";
  import whqLogoWhite from "@/images/whq-logo-color-white-horizontal.png";
  import EventBus from "@/utilities/eventBus.js";
  import { useTablePreferences } from "@/utilities/useTablePreferences.js";
  
  const appState = inject("appState");
  const confirm = useConfirm();
  const router = useRouter();
  const orderSearchTablePreferences = useTablePreferences('orderSearchDataTable', true);
  const inventorySearchTablePreferences = useTablePreferences('inventorySearchDataTable', true);

  const emit = defineEmits([ "menubutton-click", "topbaritem-click", "topbar-mobileactive", "search-click", "search-toggle", "settings-click" ]);
  const props = defineProps({
    activeTopbarItem: String,
    mobileTopbarActive: Boolean,
    searchActive: Boolean,
    menuActive: Boolean
  });
  const searchButton = ref();
  const supportButton = ref();
  const settingsButton = ref();
  const signOutButton = ref();
	const searchInputValue = ref("");
  const searchInput = ref();

  // click events for opening/closing things
  function onSearchContainerClick(event) {
    emit("search-click", event);
  }
  function onMenuButtonClick(event) {
    emit("menubutton-click", event);
  }
  function onTopbarItemClick(event, item) {
    if(item === "search") emit("search-toggle", event); 
    if(item === "settings") emit("settings-click", event);
    if(item === "support") {
      zE("webWidget", !appState.zendeskWidget.isVisible ? "open" : "close"); 
      setTimeout(() => { if (appState.zendeskWidget.isVisible) document.getElementById("webWidget").focus(); }, 200);      
    }
    if(item === "sign-out") {
      confirm.require({
        group: 'dialog',
        message: "Are you sure you want to sign out?",
        header: "Sign Out",
        icon: "material-symbols-outlined logout",
        acceptIcon: "material-symbols-outlined check",
        rejectIcon: "material-symbols-outlined close",
        acceptClass: "p-button-rounded min-w-6rem",
        rejectClass: "p-button-rounded p-button-outlined min-w-6rem",
        accept: () => {
          router.push("/signed-out");
        }
      });
    }
    emit("topbaritem-click", {originalEvent: event, item: item});
  }
	function onTopbarMobileButtonClick(event) {
    emit("topbar-mobileactive", event);
  }

  // when search is submitted, do this
  function onSearch(params) {
    emit("search-toggle");
    const searchPath = `/${params.type}/search`;
    appState.fromGlobal = true;

    router.push({
      path: searchPath,
      query: { 
        ...getBaseSearchQuery(searchPath),
        ...( params.field === "searchCriteria" ? { searchCriteria: searchInputValue.value?.trim() } : {}),
        ...( params.field === "zendeskTicket" ? { zendeskTicket: searchInputValue.value?.trim() } : {})
      }
    });
    modifierKey.value = null;
    searchInputValue.value = null;
  }

  const getBaseSearchQuery = (searchPath) => {
    let prefs;
    let result = { page: 1 };

    if (searchPath.includes('inventory')) prefs = inventorySearchTablePreferences;
    else prefs = orderSearchTablePreferences;

    result = {
      ...result,
      size: prefs.tablePreferences.value.rowsPerPage,
      sortField: prefs.tablePreferences.value.sortField,
      sortOrder: prefs.getAlphaSortOrder()
    }

    return result;
  }

  // when search panel opens, focus field and set value to "" (prevents keyboard shortcut keys from input)
  function onSearchTransitionEnter() {
    searchInput.value.$el.focus();
    setTimeout(() => { searchInputValue.value = "" });
  }

  // adding modifiers when pressing enter in search and highlighting correct buttons when held
  function onSearchEnterKey(event) {
    if (event.ctrlKey) { onSearch({ type: 'inventory', field: 'searchCriteria' }); return; }
    if (event.altKey) { onSearch({ type: 'order', field: 'zendeskTicket' }); return; }
    onSearch({ type: 'order', field: 'searchCriteria' });
  }
  const modifierKey = ref(null);
  function getSearchCriteriaPlaceholder() {
    if (modifierKey.value === "ctrl") return "Search Inventory";
    if (modifierKey.value === "alt") return "Search Zendesk Tickets";
    return "Search Criteria";
  }
  const submitButton = {
    highlight: (event) => {
      if (event.key === "Control") { modifierKey.value = "ctrl"; return; }
      if (event.key === "Alt") { modifierKey.value = "alt"; return; }
      modifierKey.value = null;
    },
    attenuate: (event) => { 
      if (event.key === "Control" && modifierKey.value === "ctrl") modifierKey.value = null;
      if (event.key === "Alt" && modifierKey.value === "alt") modifierKey.value = null;
    }
  }

  // listeners for keyboard shortcuts
  onMounted(() => {
    EventBus.on("shortcut-global-search", () => searchButton.value.$el.click());
  });
  onUnmounted(() => {
    EventBus.off("shortcut-global-search");
  });

  // badge to go up on top left to show what environment you're in
  let envBadgeText = ref();
  if (["whqdev"].includes(import.meta.env.MODE)) {
    envBadgeText.value = "DEV";
  } else if (["localhost", "whqtest" ].includes(import.meta.env.MODE)) {
    envBadgeText.value = "TEST";
  }
</script>

<style lang="scss" scoped>
  .modifier-pressed {
    outline: 3px solid rgba(var(--color-primary-rgb), 0.5);
  }
</style>